import * as LabelPrimitive from '@radix-ui/react-label'
import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const labelVariants = cva(
  'flex items-center gap-2 select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:text-muted-foreground',
  {
    variants: {
      appearance: {
        field: '',
        choice: 'cursor-pointer',
        choiceRow:
          'cursor-pointer items-start gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-muted/40',
        choiceListRow:
          'cursor-pointer items-center gap-3 rounded-none px-3 py-3 transition-colors hover:bg-muted/40',
        choicePill:
          'inline-flex shrink-0 gap-3 rounded-full border border-border/60 bg-background px-3 py-2',
        choiceCard: 'min-w-0 gap-3 rounded-xl border border-border/60 bg-background px-3 py-2',
      },
      tone: {
        default: 'text-foreground',
        muted: 'text-muted-foreground',
      },
      size: {
        default: 'text-sm',
        body: 'text-base',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
      },
      leading: {
        tight: 'leading-none',
        normal: 'leading-normal',
        relaxed: 'leading-5',
      },
    },
    defaultVariants: {
      appearance: 'field',
      tone: 'default',
      size: 'default',
      weight: 'medium',
      leading: 'tight',
    },
  }
)

export type LabelProps = ComponentProps<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants>

function Label({
  appearance = 'field',
  className,
  leading = 'tight',
  size = 'default',
  tone = 'default',
  weight = 'medium',
  ...props
}: LabelProps) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      data-appearance={appearance}
      data-size={size}
      data-tone={tone}
      data-weight={weight}
      className={cn(labelVariants({ appearance, leading, size, tone, weight }), className)}
      {...props}
    />
  )
}

export { Label, labelVariants }
